/**
 * Optimized Context Provider - Combines related contexts and adds performance optimizations
 * Reduces context switching and unnecessary re-renders
 */

import React, { createContext, useContext, useMemo, useEffect, ReactNode } from 'react';
import { WindowManagerProvider, useWindowManager } from './WindowManager';
import { WidgetManagerProvider, useWidgetManager } from './WidgetManager';
import { PreferencesProvider, usePreferences } from './PreferencesContext';
import { useAppsManager } from './AppsManager';
import { chatActiveStore } from '@/lib/chatActiveStore';
import { soundstageActiveStore } from '@/lib/soundstageActiveStore';
import PerformanceMonitor from '@/utils/PerformanceMonitor';

// Combined context type for better performance
interface OptimizedAppContextType {
  // Apps Manager - now managed externally
  apps: any[];
  installedApps: any[];
  dockApps: any[];
  unifiedDock: any[];
  runningApps: string[];
  
  // Window Manager
  windows: ReturnType<typeof useWindowManager>['windows'];
  activeWindow: ReturnType<typeof useWindowManager>['activeWindow'];
  
  // Widget Manager  
  widgets: ReturnType<typeof useWidgetManager>['widgets'];
  
  // Preferences
  backgroundType: ReturnType<typeof usePreferences>['backgroundType'];
  timeMode: ReturnType<typeof usePreferences>['timeMode'];
  
  // Performance
  performanceSettings: {
    shouldUseLowQuality: boolean;
    shouldPauseAnimations: boolean;
    recommendedFrameRate: number;
  };
  
  // Actions (memoized)
  actions: {
    // Window actions  
    openWindow: ReturnType<typeof useWindowManager>['openWindow'];
    closeWindow: (id: string) => void;
    focusWindow: (id: string) => void;
    
    // Widget actions
    openWidget: ReturnType<typeof useWidgetManager>['openWidget'];
    closeWidget: (id: string) => void;
    
    // Preference actions
    setBackgroundType: ReturnType<typeof usePreferences>['setBackgroundType'];
    setTimeMode: ReturnType<typeof usePreferences>['setTimeMode'];
  };
}

const OptimizedAppContext = createContext<OptimizedAppContextType | null>(null);

// Memoized component to prevent unnecessary re-renders
const OptimizedContextInner = React.memo(({ children }: { children: ReactNode }) => {
  // Note: AppsManager is now provided outside this context
  // const appsManager = useAppsManager();
  const windowManager = useWindowManager();
  const widgetManager = useWidgetManager();
  const preferences = usePreferences();
  
  // Performance monitoring
  const performanceMonitor = useMemo(() => PerformanceMonitor.getInstance(), []);
  const [performanceSettings, setPerformanceSettings] = React.useState({
    shouldUseLowQuality: false,
    shouldPauseAnimations: false,
    recommendedFrameRate: 60,
  });

  // Subscribe to performance updates
  React.useEffect(() => {
    performanceMonitor.startMonitoring();
    
    const unsubscribe = performanceMonitor.subscribe((metrics, settings) => {
      setPerformanceSettings({
        shouldUseLowQuality: metrics.fps < 30 || metrics.gpuTier === 'low',
        shouldPauseAnimations: metrics.fps < 20,
        recommendedFrameRate: settings.animationFrameRate,
      });
    });

    return () => {
      unsubscribe();
      performanceMonitor.stopMonitoring();
    };
  }, [performanceMonitor]);

  // Memoized actions to prevent unnecessary re-renders
  const actions = useMemo(() => ({
    // App actions - now handled externally
    // openApp: appsManager.openApp,
    // closeApp: appsManager.closeApp,
    // installApp: appsManager.installApp,
    
    // Window actions
    openWindow: windowManager.openWindow,
    closeWindow: windowManager.closeWindow,
    focusWindow: windowManager.focusWindow,
    
    // Widget actions
    openWidget: widgetManager.openWidget,
    closeWidget: widgetManager.closeWidget,
    
    // Preference actions
    setBackgroundType: preferences.setBackgroundType,
    setTimeMode: preferences.setTimeMode,
  }), [
    // appsManager.openApp, appsManager.closeApp, appsManager.installApp,
    windowManager.openWindow, windowManager.closeWindow, windowManager.focusWindow,
    widgetManager.openWidget, widgetManager.closeWidget,
    preferences.setBackgroundType, preferences.setTimeMode,
  ]);

  // Memoized context value
  const contextValue = useMemo(() => ({
    // Apps Manager data - now handled externally
    // apps: appsManager.apps,
    // installedApps: appsManager.installedApps,
    // dockApps: appsManager.dockApps,
    // unifiedDock: appsManager.unifiedDock,
    // runningApps: appsManager.runningApps,
    apps: [],
    installedApps: [],
    dockApps: [],
    unifiedDock: [],
    runningApps: [],
    
    // Window Manager data
    windows: windowManager.windows,
    activeWindow: windowManager.activeWindow,
    
    // Widget Manager data
    widgets: widgetManager.widgets,
    
    // Preferences data
    backgroundType: preferences.backgroundType,
    timeMode: preferences.timeMode,
    
    // Performance data
    performanceSettings,
    
    // Actions
    actions,
  }), [
    // appsManager.apps, appsManager.installedApps, appsManager.dockApps, 
    // appsManager.unifiedDock, appsManager.runningApps,
    windowManager.windows, windowManager.activeWindow,
    widgetManager.widgets,
    preferences.backgroundType, preferences.timeMode,
    performanceSettings,
    actions,
  ]);

  return (
    <OptimizedAppContext.Provider value={contextValue}>
      {children}
    </OptimizedAppContext.Provider>
  );
});

OptimizedContextInner.displayName = 'OptimizedContextInner';

/**
 * Bridge component that syncs WindowManager state to chatActiveStore.
 * Tracks whether the chat window exists (open or minimized) so that
 * ChatWebSocketProvider (higher in the tree) can gate socket connections.
 */
function ChatActiveBridge() {
  const { windows } = useWindowManager();
  const { apps } = useAppsManager();

  useEffect(() => {
    const chatAppIds = new Set(
      apps.filter((a) => a.component_type === 'TeamChatWindow').map((a) => a.id)
    );
    // Check both UUID (from dock) and derived ID (from App Store: 'teamchat')
    const hasChatWindow = windows.some(
      (w) => chatAppIds.has(w.id) || w.id === 'teamchat'
    );
    chatActiveStore.setActive(hasChatWindow);
  }, [windows, apps]);

  // Cleanup on unmount (e.g. logout)
  useEffect(() => {
    return () => chatActiveStore.setActive(false);
  }, []);

  return null;
}

/**
 * Bridge component that syncs WindowManager state to soundstageActiveStore.
 * Tracks whether the SoundStage window exists (open or minimized) so that
 * SoundstageWebSocketProvider (higher in the tree) can gate socket connections.
 */
function SoundstageActiveBridge() {
  const { windows } = useWindowManager();
  const { apps } = useAppsManager();

  useEffect(() => {
    const soundstageAppIds = new Set(
      apps.filter((a) => a.component_type === 'SoundStageWindow').map((a) => a.id)
    );
    // Check both UUID (from dock) and derived ID (from App Store: 'soundstage')
    const hasSoundstageWindow = windows.some(
      (w) => soundstageAppIds.has(w.id) || w.id === 'soundstage'
    );
    soundstageActiveStore.setActive(hasSoundstageWindow);
  }, [windows, apps]);

  useEffect(() => {
    return () => soundstageActiveStore.setActive(false);
  }, []);

  return null;
}

/**
 * Optimized provider that combines all contexts with performance optimizations
 * Note: AppsManagerProvider is excluded as it depends on OrganizationProvider
 * PreferencesProvider handles organization-specific settings internally via localStorage
 */
export const OptimizedContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <PreferencesProvider>
      <WindowManagerProvider>
        <ChatActiveBridge />
        <SoundstageActiveBridge />
        <WidgetManagerProvider>
          <OptimizedContextInner>
            {children}
          </OptimizedContextInner>
        </WidgetManagerProvider>
      </WindowManagerProvider>
    </PreferencesProvider>
  );
};

/**
 * Optimized hook that provides access to all app state with performance benefits
 */
export const useOptimizedApp = () => {
  const context = useContext(OptimizedAppContext);
  if (!context) {
    throw new Error('useOptimizedApp must be used within OptimizedContextProvider');
  }
  return context;
};

/**
 * Selective hooks for when you only need specific parts of the state
 * These prevent unnecessary re-renders by only subscribing to relevant changes
 */

export const useOptimizedApps = () => {
  // Apps are now managed externally, return empty for backwards compatibility
  return useMemo(() => ({
    apps: [],
    installedApps: [],
    dockApps: [],
    unifiedDock: [],
    runningApps: [],
    openApp: () => console.warn('openApp moved to AppsManager'),
    closeApp: () => console.warn('closeApp moved to AppsManager'),
    installApp: () => console.warn('installApp moved to AppsManager'),
  }), []);
};

export const useOptimizedWindows = () => {
  const context = useOptimizedApp();
  return useMemo(() => ({
    windows: context.windows,
    activeWindow: context.activeWindow,
    openWindow: context.actions.openWindow,
    closeWindow: context.actions.closeWindow,
    focusWindow: context.actions.focusWindow,
  }), [context.windows, context.activeWindow, context.actions.openWindow, 
       context.actions.closeWindow, context.actions.focusWindow]);
};

export const useOptimizedPreferences = () => {
  const context = useOptimizedApp();
  return useMemo(() => ({
    backgroundType: context.backgroundType,
    timeMode: context.timeMode,
    setBackgroundType: context.actions.setBackgroundType,
    setTimeMode: context.actions.setTimeMode,
  }), [context.backgroundType, context.timeMode, context.actions.setBackgroundType, 
       context.actions.setTimeMode]);
};

export const useOptimizedPerformance = () => {
  const context = useOptimizedApp();
  return context.performanceSettings;
};